import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Navbar from "../admin/AdminNav";
import QuestionForm from "./components/QuestionForm";
import { useQuestionsApi } from "./components/useQuestions";
import api from "../api/api";
import Footer from "../Footer";

const AdminQA = () => {

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    })
  );
  const {
    getQuestions,
    saveQuestion,
    deleteQuestion,
    deleteCategory,
    getCategories,
  } = useQuestionsApi();

  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editData, setEditData] = useState(null);

  const [categoryName, setCategoryName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [categoryError, setCategoryError] = useState("");
const [isReordering, setIsReordering] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    categoryId: null,
  });

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const closeToast = () => setToast({ ...toast, open: false });

  // ================= FETCH =================
  const fetchData = async () => {
    try {
      const [qData, cData] = await Promise.all([
        getQuestions(),
        getCategories(),
      ]);

      setQuestions(qData || []);
      setCategories(cData || []);
    } catch {
      showToast("Failed to load data", "error");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const categoryUsage = useMemo(() => {
    const map = {};
    questions.forEach((q) => {
      map[q.category] = (map[q.category] || 0) + 1;
    });
    return map;
  }, [questions]);

  // ================= SAVE QUESTION =================
  const handleSave = async (q) => {
    try {
      if (!q.category) {
        showToast("Category is required", "error");
        return;
      }

      await saveQuestion({
        questionId: q.questionId || 0,
        questionText: q.questionText,
        answerText: q.answerText,
        category: q.category,
        position: q.position || 0,
      });
      // live update
      setQuestions((prev) =>
        prev.map((x) => (x.questionId === q.questionId ? { ...x, ...q } : x)),
      );

      showToast("Saved successfully!");
      await fetchData();
    } catch (err) {
      console.error(err);
      showToast("Failed to save", "error");
    }
  };
const handleQuestionDragEnd = async (event) => {
  const { active, over } = event;

  if (!over || active.id === over.id) return;

  const sortedQuestions = [...questions].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  );

  const oldIndex = sortedQuestions.findIndex(
    (q) => q.questionId === active.id
  );
  const newIndex = sortedQuestions.findIndex(
    (q) => q.questionId === over.id
  );

  const updated = [...sortedQuestions];
  const [moved] = updated.splice(oldIndex, 1);
  updated.splice(newIndex, 0, moved);

  const withPosition = updated.map((q, i) => ({
    ...q,
    position: i + 1,
  }));

  setQuestions(withPosition);

  try {
    setIsReordering(true);

    for (let i = 0; i < withPosition.length; i++) {
      await saveQuestion(withPosition[i]);
    }

    showToast("Updating order...");
    await fetchData();
    showToast("Question order updated");
  } catch (err) {
    console.error(err);
    showToast("Failed to update order", "error");
  } finally {
    setIsReordering(false);
  }
};
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    //  USE SORTED ARRAY (IMPORTANT)
    const oldIndex = sortedCategories.findIndex(
      (c) => c.questionId === active.id,
    );
    const newIndex = sortedCategories.findIndex(
      (c) => c.questionId === over.id,
    );

    const updated = [...sortedCategories];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);

    const withPosition = updated.map((c, i) => ({
      ...c,
      position: i + 1,
    }));

    setCategories(withPosition);

    try {
      for (let i = 0; i < withPosition.length; i++) {
        await api.post("/api/Admin/AddOrUpdateQuestionMaster", {
          questionId: withPosition[i].questionId,
          questionName: withPosition[i].questionName,
          position: withPosition[i].position,
        });
      }

      await fetchData();
      showToast("Category order updated");
    } catch (err) {
      console.error(err);
      showToast("Failed to update order", "error");
    }
  };
  const handleDelete = async (id) => {
    try {
      await deleteQuestion(id);
      setQuestions((prev) => prev.filter((q) => q.questionId !== id));
      showToast("Deleted successfully!");
      await fetchData();
    } catch {
      showToast("Delete failed", "error");
    }
  };

  const handleEdit = (q) => {
    setEditData({
      questions: [
        {
          questionId: q.questionId,
          questionText: q.questionText,
          answerText: q.answerText,
          category: q.category, //  FIXED
        },
      ],
    });
  };

  // ================= CATEGORY =================
  const handleSaveCategory = async () => {
    const trimmed = categoryName.trim();

    if (!trimmed) {
      setCategoryError("Category cannot be empty");
      return;
    }

    const duplicate = categories.some(
      (c) =>
        c.questionName.toLowerCase() === trimmed.toLowerCase() &&
        c.questionId !== editCategoryId,
    );

    if (duplicate) {
      setCategoryError("Category already exists");
      return;
    }

    await api.post("/api/Admin/AddOrUpdateQuestionMaster", {
      questionId: editCategoryId || 0,
      questionName: trimmed,
      position: editCategoryId
        ? categories.findIndex((c) => c.questionId === editCategoryId) + 1
        : categories.length + 1,
    });
    const newCat = {
      questionId: editCategoryId || Date.now(),
      questionName: trimmed,
      position: editCategoryId
        ? categories.find((c) => c.questionId === editCategoryId)?.position
        : categories.length + 1,
    };

    if (editCategoryId) {
      setCategories((prev) =>
        prev.map((c) => (c.questionId === editCategoryId ? newCat : c)),
      );
    } else {
      setCategories((prev) => [...prev, newCat]);
    }

    showToast(
      editCategoryId
        ? "Category updated successfully"
        : "Category added successfully",
    );

    setCategoryName("");
    setEditCategoryId(null);
    setCategoryError("");
    await fetchData(); //  refresh categories + questions
  };

  const handleEditCategory = (cat) => {
    setCategoryName(cat.questionName);
    setEditCategoryId(cat.questionId);
  };

  const confirmDeleteCategory = async () => {
    await deleteCategory(deleteDialog.categoryId);

    setCategories((prev) =>
      prev.filter((c) => c.questionId !== deleteDialog.categoryId),
    );
    await fetchData();
    showToast("Category deleted successfully");
    setDeleteDialog({ open: false, categoryId: null });
  };
  const sortedCategories = [...categories].sort(
    (a, b) => (a.position || 0) - (b.position || 0),
  );
  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: 800, m: "auto", p: 3 }}>
        <Typography variant="h4">Q&A Management</Typography>

        {/* CATEGORY */}
        <Paper sx={{ p: 3, my: 3 }}>
          <TextField
            fullWidth
            label="Category"
            value={categoryName}
            onChange={(e) => {
              setCategoryName(e.target.value);
              setCategoryError("");
            }}
            error={!!categoryError}
            helperText={categoryError}
          />

          <Button
            onClick={handleSaveCategory}
            disabled={!categoryName.trim()}
            sx={{ mt: 2 }}
          >
            {editCategoryId ? "Update" : "Add"}
          </Button>
          <DndContext
             sensors={sensors}
  collisionDetection={closestCenter}
  onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext
              items={sortedCategories.map((c) => c.questionId)}
              strategy={verticalListSortingStrategy}
            >
              {sortedCategories.map((cat) => {
                const isUsed = categoryUsage[cat.questionName];

                return (
                  <SortableCategoryItem
                    key={cat.questionId}
                    id={cat.questionId}
                    cat={cat}
                    onEdit={handleEditCategory}
                    onDelete={setDeleteDialog}
                    isUsed={isUsed}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </Paper>

        {/* FORM */}
        <QuestionForm
          key={JSON.stringify(categories)}
          onSubmit={handleSave}
          editData={editData}
          setEditData={setEditData}
          categories={categories}
        />

        {isReordering && (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    }}
  >
    <CircularProgress />
  </Box>
)}

        {/* QUESTIONS */}
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleQuestionDragEnd}
        >
          <SortableContext
            items={questions.map((q) => q.questionId)}
            strategy={verticalListSortingStrategy}
          >
            {[...questions]
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((q) => (
                <SortableQuestionItem
                  key={q.questionId}
                  id={q.questionId}
                  q={q}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
          </SortableContext>
        </DndContext>

        {/* DELETE DIALOG */}
        <Dialog open={deleteDialog.open}>
          <DialogTitle>Delete Category?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false })}>
              Cancel
            </Button>
            <Button color="error" onClick={confirmDeleteCategory}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={2000}
          onClose={closeToast}
        >
          <Alert severity={toast.severity}>{toast.message}</Alert>
        </Snackbar>
      </Box>
      <Footer />
    </>
  );
};
const SortableCategoryItem = ({ id, cat, onEdit, onDelete, isUsed }) => {
  const { setNodeRef, transform, transition, attributes, listeners } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 1, mt: 1 }}>
<Box
  display="flex"
  justifyContent="space-between"
  alignItems="center"
  flexWrap="wrap"
  gap={1}
>
        {/* LEFT: Drag handle + text */}
        <Box display="flex" alignItems="center" gap={1}>
          <DragIndicatorIcon
            {...attributes}
            {...listeners}
            sx={{
  cursor: "grab",
  touchAction: "none",
}}
          />
          <Typography>{cat.questionName}</Typography>
        </Box>

        {/* RIGHT: actions */}
        <Box>
          <IconButton disabled={isUsed} onClick={() => onEdit(cat)}>
            <Edit color={isUsed ? "disabled" : "inherit"} />
          </IconButton>

          <IconButton
            disabled={isUsed}
            onClick={() => onDelete({ open: true, categoryId: cat.questionId })}
          >
            <Delete color={isUsed ? "disabled" : "error"} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};
const SortableQuestionItem = ({ id, q, onEdit, onDelete }) => {
  const { setNodeRef, transform, transition, attributes, listeners } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
<Paper
  ref={setNodeRef}
  style={style}
  sx={{
    p: { xs: 1.5, sm: 2 },
    mt: 2,
  }}
>
<Box
  display="flex"
  alignItems="center"
  gap={1}
  flexWrap="wrap"
>
        <DragIndicatorIcon
          {...attributes}
          {...listeners}
          sx={{
  cursor: "grab",
  touchAction: "none",
}}
        />
        <Chip label={q.category} />
      </Box>

      <Typography>{q.questionText}</Typography>
      <Typography>{q.answerText}</Typography>

      <IconButton onClick={() => onEdit(q)}>
        <Edit />
      </IconButton>

      <IconButton onClick={() => onDelete(q.questionId)}>
        <Delete color="error" />
      </IconButton>
    </Paper>
  );
};

export default AdminQA;
